import { API_BASE_URL, getAuthHeaders } from '../../api/mediaUrl';

/** Party types a challan can be sent to (same order as on the form and PDF). */
export const CHALLAN_PARTY_TYPES = [
  { key: 'BUTTA_CUTTING_PARTIES', label: 'Butta Cutting', short: 'Butta' },
  { key: 'MILL_FINISH_PARTIES', label: 'Mill Finish', short: 'Mill' },
  { key: 'BORDER_CUTTING_PARTIES', label: 'Border Cutting', short: 'Border' },
  { key: 'HANDWORK_PARTIES', label: 'Handwork', short: 'Handwork' },
];

export const CHALLAN_TYPE_LABEL = Object.fromEntries(CHALLAN_PARTY_TYPES.map((t) => [t.key, t.label]));

/** Most takas on one challan; the PDF prints 18 + 18 rows up to 36, 24 + 24 up to 48. */
export const MAX_CHALLAN_ROWS = 48;

/** Indian GSTIN, e.g. 24ABCDE1234F1Z5 (same rule as the backend). */
export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export const normalizeGstNo = (value) => String(value || '').replace(/\s+/g, '').toUpperCase();

export const challansListPath = () => '/challans';
export const challansNewPath = () => '/challans/new';
export const challanEditPath = (id) => `/challans/${id}`;

/** Today's date as YYYY-MM-DD in the browser's time zone. */
export function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return d && m && y ? `${d}/${m}/${y}` : String(iso);
}

export const formatMeters = (n) => (Number(n) || 0).toFixed(2);

/** File name used for downloads, e.g. Challan-14-2026-27-Butta-Cutting.pdf */
export function challanPdfFileName(challan, copy) {
  const suffix = copy ? `-${(CHALLAN_TYPE_LABEL[copy] || copy).replace(/\s+/g, '-')}` : '';
  const fy = challan.financialYear ? `-${challan.financialYear}` : '';
  return `Challan-${challan.challanNo}${fy}${suffix}.pdf`;
}

/**
 * Loads the challan PDF from the server (all party copies, or one copy). The preview and the
 * download use this same file, so what the user sees is exactly what is downloaded.
 * @param {{ id: string }} challan
 * @param {string} [copy] party type key, e.g. BUTTA_CUTTING_PARTIES
 * @returns {Promise<Blob>}
 */
export async function fetchChallanPdfBlob(challan, copy) {
  const qs = copy ? `?copy=${encodeURIComponent(copy)}` : '';
  const res = await fetch(`${API_BASE_URL}/api/challans/${challan.id}/pdf${qs}`, { headers: getAuthHeaders() });
  if (!res.ok) {
    let message = `PDF could not be loaded (${res.status})`;
    try {
      const data = await res.json();
      message = data?.error?.message || data?.error || data?.message || message;
    } catch {
      // not JSON
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
}

/** Saves a PDF blob with the given file name. */
export function saveBlobAs(blob, fileName) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/**
 * Downloads the challan PDF (all party copies, or one copy).
 * @param {{ id: string, challanNo: number, financialYear?: string }} challan
 * @param {string} [copy] party type key, e.g. BUTTA_CUTTING_PARTIES
 */
export async function downloadChallanPdf(challan, copy) {
  const blob = await fetchChallanPdfBlob(challan, copy);
  saveBlobAs(blob, challanPdfFileName(challan, copy));
}
